// ante-react-kit/src/components/v2/AntAddressField/index.js
// Ô địa chỉ hợp nhất trên AntBaseForm: hiển thị thuần thông tin + nút Sửa → floating panel 2 chế độ.
// props: prefix, values (formData), disabled, label, API, onChange(obj đa field), country, locale
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './index.scss';
import AntLocalization from '../../../utils/AntLocalization';
import AntAPI from '../../../utils/AntAPI';
import AntAddress from '../../../utils/AntAddress';
import AntLocalStorage from '../../../utils/AntLocalStorage';
import AntAddressSearch from '../AntAddressSearch';
import { useAddressSearchEnabled } from '../AntAddressSearch/capability';

var LEVELS = [1, 2, 3];
var codeKey = function (p, l) { return p + 'adm' + l + '_code'; };
var nameKey = function (p, l) { return p + 'adm' + l + '_name'; };

export default function AntAddressField(props) {
    var prefix = props.prefix || 'entity_';
    var values = props.values || {};
    var country = useMemo(function () { return props.country || AntLocalStorage.getBusinessSetting('Sys_BaseCountry') || 'VN'; }, [props.country]);
    var locale = useMemo(function () { return props.locale || AntLocalStorage.getBusinessSetting('Sys_BaseLanguage') || 'vi'; }, [props.locale]);
    var api = useMemo(function () { return props.API || new AntAPI('sys', 'SysAddress'); }, [props.API]);

    var [loadTick, setLoadTick] = useState(0);
    var addr = useMemo(function () { return new AntAddress(country, locale); }, [country, locale, loadTick]);
    useEffect(function () {
        if (!addr.isLoaded()) AntAddress.ensure(country, api).then(function () { setLoadTick(function (n) { return n + 1; }); });
    }, []); // eslint-disable-line

    var levelCount = addr.isLoaded() ? addr.getLevelCount() : 3;
    var mode = addr.getMode();

    var [open, setOpen] = useState(false);
    var [draft, setDraft] = useState({});
    // Máy chủ chưa khai khoá tìm địa chỉ → ẩn thẻ "Tìm nhanh", chỉ còn "Chọn theo cấp".
    var searchEnabled = useAddressSearchEnabled(country);
    var [tab, setTab] = useState('search');
    var [browseLevel, setBrowseLevel] = useState(1);
    var [filter, setFilter] = useState('');
    var rootRef = useRef(null);

    // Popup nổi giữa màn hình (portal ra body) — đóng bằng phím Esc; khoá cuộn nền khi mở.
    useEffect(function () {
        if (!open) return;
        var onKey = function (e) { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', onKey);
        var prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return function () {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    var summaryOf = useCallback(function (src, postal) {
        var parts = [];
        if (src[prefix + 'address'] || src.address) parts.push(src[prefix + 'address'] || src.address);
        for (var l = levelCount; l >= 1; l--) { var nm = src[nameKey(prefix, l)] || src['adm' + l + '_name']; if (nm) parts.push(nm); }
        if (mode === 'postal' && (src[prefix + 'postal_code'] || src.postal_code)) parts.push(src[prefix + 'postal_code'] || src.postal_code);
        return parts.join(', ');
    }, [prefix, levelCount, mode]);

    var summary = summaryOf(values);

    var openEditor = useCallback(function () {
        var d = { address: values[prefix + 'address'] || '', postal_code: values[prefix + 'postal_code'] || '' };
        LEVELS.forEach(function (l) {
            d['adm' + l + '_code'] = values[codeKey(prefix, l)] || '';
            d['adm' + l + '_name'] = values[nameKey(prefix, l)] || '';
        });
        setDraft(d);
        setTab('search');
        setFilter('');
        var start = 1;
        for (var l = 1; l <= levelCount; l++) { if (!d['adm' + l + '_code']) { start = l; break; } start = Math.min(l + 1, levelCount); }
        setBrowseLevel(start);
        setOpen(true);
    }, [values, prefix, levelCount]);

    var onSearchPicked = useCallback(function (obj) {
        setDraft(function (v) {
            var d = Object.assign({}, v, { address: obj[prefix + 'address'] || v.address, postal_code: obj[prefix + 'postal_code'] || v.postal_code });
            LEVELS.forEach(function (l) {
                d['adm' + l + '_code'] = obj[codeKey(prefix, l)] || '';
                d['adm' + l + '_name'] = obj[nameKey(prefix, l)] || '';
            });
            return d;
        });
    }, [prefix]);

    var listForLevel = useCallback(function (L) {
        if (!addr.isLoaded()) return [];
        if (L === 1) return addr.getProvinces();
        if (L === 2) return addr.getDistricts(draft.adm1_code);
        if (L === 3) return addr.getWards(draft.adm2_code);
        return [];
    }, [addr, draft.adm1_code, draft.adm2_code]);

    var pickLevel = useCallback(function (L, item) {
        setDraft(function (v) {
            var d = Object.assign({}, v);
            d['adm' + L + '_code'] = item.code;
            d['adm' + L + '_name'] = item.name;
            for (var k = L + 1; k <= 3; k++) { d['adm' + k + '_code'] = ''; d['adm' + k + '_name'] = ''; }
            return d;
        });
        setFilter('');
        if (L < levelCount) setBrowseLevel(L + 1);
    }, [levelCount]);

    var confirm = useCallback(function () {
        var obj = {};
        obj[prefix + 'address'] = draft.address || null;
        LEVELS.forEach(function (l) {
            obj[codeKey(prefix, l)] = draft['adm' + l + '_code'] || null;
            obj[nameKey(prefix, l)] = draft['adm' + l + '_name'] || null;
        });
        if (mode === 'postal') obj[prefix + 'postal_code'] = draft.postal_code || null;
        if (props.onChange) props.onChange(obj);
        setOpen(false);
    }, [draft, prefix, mode, props]);

    var levelLabel = function (L) {
        return addr.getLevelLabelText(L) || AntLocalization.translateText([{ vi: '', en: '' }, { vi: 'Tỉnh/Thành', en: 'Province', fr: 'Province', es: 'Provincia', id: 'Provinsi', ja: '省/市', zh: '省/市', ko: '도/시', hi: 'प्रांत', ar: 'المحافظة' }, { vi: 'Quận/Huyện', en: 'District', fr: 'District', es: 'Distrito', id: 'Distrik', ja: '区/郡', zh: '区/县', ko: '구/군', hi: 'ज़िला', ar: 'المنطقة' }, { vi: 'Phường/Xã', en: 'Ward', fr: 'Quartier', es: 'Barrio', id: 'Kelurahan', ja: '町/村', zh: '街道/乡', ko: '동/면', hi: 'वार्ड', ar: 'الحي' }][L]);
    };

    var rawList = tab === 'cascade' ? listForLevel(browseLevel) : [];
    var list = filter ? rawList.filter(function (x) { return String(x.name || '').toLowerCase().indexOf(filter.toLowerCase()) >= 0; }) : rawList;

    var t = AntLocalization.translateText;

    return (
        <div className="v2-addr-field" ref={rootRef}>
            {props.label ? <label className="v2-addr-field__label">{t(props.label)}</label> : null}

            <div className={'v2-addr-field__summary' + (props.disabled ? ' is-disabled' : '')} onClick={props.disabled ? undefined : openEditor}>
                <span className="v2-addr-field__pin">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                </span>
                <span className={'v2-addr-field__summary-text' + (summary ? '' : ' is-empty')}>{summary || t({ vi: 'Chọn địa chỉ', en: 'Select address', fr: 'Choisir adresse', es: 'Elegir dirección', id: 'Pilih alamat', ja: '住所を選択', zh: '选择地址', ko: '주소 선택', hi: 'पता चुनें', ar: 'اختر العنوان' })}</span>
                {!props.disabled ? <span className="v2-addr-field__edit">{t({ vi: 'Sửa', en: 'Edit', fr: 'Modif.', es: 'Editar', id: 'Ubah', ja: '編集', zh: '编辑', ko: '수정', hi: 'संपादन', ar: 'تعديل' })}</span> : null}
            </div>

            {open ? ReactDOM.createPortal(
                <div className="v2-addr-field__overlay" onMouseDown={function (e) { if (e.target === e.currentTarget) setOpen(false); }}>
                <div className="v2-addr-field__panel v2-addr-field__panel--modal">
                    <div className="v2-addr-field__head">
                        <span className="v2-addr-field__title">{t(props.label) || t({ vi: 'Chọn địa chỉ', en: 'Select address', fr: 'Choisir adresse', es: 'Elegir dirección', id: 'Pilih alamat', ja: '住所を選択', zh: '选择地址', ko: '주소 선택', hi: 'पता चुनें', ar: 'اختر العنوان' })}</span>
                        <button type="button" className="v2-addr-field__close" onClick={function () { setOpen(false); }} aria-label="Close">×</button>
                    </div>
                    <div className="v2-addr-field__current">{summaryOf(draft) || t({ vi: 'Chưa có địa chỉ', en: 'No address yet', fr: 'Aucune adresse', es: 'Sin dirección', id: 'Belum ada alamat', ja: '住所なし', zh: '暂无地址', ko: '주소 없음', hi: 'पता नहीं है', ar: 'لا يوجد عنوان' })}</div>

                    {/* Số nhà/đường (và mã bưu chính) đặt TRÊN CÙNG: đây là phần người dùng luôn phải gõ,
                        và giữ nguyên khi đổi qua lại giữa hai cách chọn. Trước đây ô này nằm dưới và bị
                        lặp ở cả hai thẻ — dễ nhầm là hai ô khác nhau. */}
                    <div className="v2-addr-field__body v2-addr-field__body--street">
                        <label className="v2-addr-field__sub">{t({ vi: 'Số nhà, đường', en: 'Street address', fr: 'N° et rue', es: 'Calle y número', id: 'Jalan, nomor', ja: '番地・通り', zh: '街道门牌', ko: '도로명 주소', hi: 'मकान, सड़क', ar: 'الشارع والرقم' })}</label>
                        <input className="v2-addr-field__input" value={draft.address || ''} onChange={function (e) { var val = e.target.value; setDraft(function (v) { return Object.assign({}, v, { address: val }); }); }} />
                        {mode === 'postal' || !levelCount ? (
                            <Fragment>
                                <label className="v2-addr-field__sub">{t({ vi: 'Mã bưu chính', en: 'Postal code', fr: 'Code postal', es: 'Código postal', id: 'Kode pos', ja: '郵便番号', zh: '邮政编码', ko: '우편번호', hi: 'पिन कोड', ar: 'الرمز البريدي' })}</label>
                                <input className="v2-addr-field__input" value={draft.postal_code || ''} onChange={function (e) { var val = e.target.value; setDraft(function (v) { return Object.assign({}, v, { postal_code: val }); }); }} />
                            </Fragment>
                        ) : null}
                    </div>

                    {/* Thẻ chỉ hiện khi thực sự có cách chọn: nước không chia cấp thì bỏ "Chọn theo cấp",
                        máy chủ chưa khai khoá thì bỏ "Tìm nhanh". Không còn thẻ nào thì ẩn cả dãy. */}
                    {searchEnabled || levelCount > 0 ? (
                        <div className="v2-addr-field__tabs">
                            {searchEnabled ? (
                                <button type="button" className={'v2-addr-field__tab' + (tab === 'search' ? ' is-active' : '')} onClick={function () { setTab('search'); }}>{t({ vi: 'Tìm nhanh', en: 'Quick search', fr: 'Recherche', es: 'Búsqueda', id: 'Cari cepat', ja: '検索', zh: '快速搜索', ko: '빠른 검색', hi: 'त्वरित खोज', ar: 'بحث سريع' })}</button>
                            ) : null}
                            {levelCount > 0 ? (
                                <button type="button" className={'v2-addr-field__tab' + (tab === 'cascade' || !searchEnabled ? ' is-active' : '')} onClick={function () { setTab('cascade'); }}>{t({ vi: 'Chọn theo cấp', en: 'By level', fr: 'Par niveau', es: 'Por nivel', id: 'Per tingkat', ja: '階層別', zh: '按级选择', ko: '단계별', hi: 'स्तर अनुसार', ar: 'حسب المستوى' })}</button>
                            ) : null}
                        </div>
                    ) : null}

                    {tab === 'search' && searchEnabled ? (
                        <div className="v2-addr-field__body">
                            <AntAddressSearch country={country} language={locale} onSelect={onSearchPicked} />
                        </div>
                    ) : levelCount > 0 ? (
                        <div className="v2-addr-field__body">
                            <div className="v2-addr-field__crumbs">
                                {Array.from({ length: levelCount }, function (_, i) { return i + 1; }).map(function (l) {
                                    var chosen = draft['adm' + l + '_name'];
                                    return (
                                        <button type="button" key={l} className={'v2-addr-field__crumb' + (browseLevel === l ? ' is-active' : '') + (chosen ? '' : ' is-empty')} onClick={function () { setBrowseLevel(l); setFilter(''); }}>
                                            {chosen || levelLabel(l)}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Mã bưu chính và số nhà đã nằm trên cùng — dưới đây chỉ còn việc chọn cấp. */}
                            <input className="v2-addr-field__filter" value={filter} onChange={function (e) { setFilter(e.target.value); }} placeholder={t({ vi: 'Lọc', en: 'Filter', fr: 'Filtrer', es: 'Filtrar', id: 'Saring', ja: '絞込', zh: '筛选', ko: '필터', hi: 'फ़िल्टर', ar: 'تصفية' }) + ' ' + String(levelLabel(browseLevel)).toLowerCase()} />
                            <div className="v2-addr-field__list">
                                {list.length === 0 ? (
                                    <div className="v2-addr-field__empty">
                                        {browseLevel > 1 && !draft['adm' + (browseLevel - 1) + '_code'] ? t({ vi: 'Chọn cấp trên trước', en: 'Select the level above first', fr: 'Choisir d\'abord le parent', es: 'Elija primero el superior', id: 'Pilih tingkat atas dulu', ja: '先に上位を選択', zh: '请先选择上级', ko: '상위 단계를 먼저 선택', hi: 'पहले ऊपरी स्तर चुनें', ar: 'اختر المستوى الأعلى أولاً' }) : t({ vi: 'Không có dữ liệu', en: 'No data', fr: 'Aucune donnée', es: 'Sin datos', id: 'Tidak ada data', ja: 'データなし', zh: '暂无数据', ko: '데이터 없음', hi: 'कोई डेटा नहीं', ar: 'لا توجد بيانات' })}
                                    </div>
                                ) : list.map(function (item) {
                                    var selected = draft['adm' + browseLevel + '_code'] === item.code;
                                    return (
                                        <div key={item.code} className={'v2-addr-field__row' + (selected ? ' is-selected' : '')} onClick={function () { pickLevel(browseLevel, item); }}>
                                            <span>{item.name}</span>
                                            {selected ? <span className="v2-addr-field__check">✓</span> : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    <div className="v2-addr-field__actions">
                        <button type="button" className="v2-addr-field__cancel" onClick={function () { setOpen(false); }}>{t({ vi: 'Huỷ', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء', ja: '取消' })}</button>
                        <button type="button" className="v2-addr-field__confirm" onClick={confirm}>{t({ vi: 'Xác nhận', en: 'Confirm', fr: 'Confirmer', es: 'Confirmar', id: 'Konfirmasi', ja: '確認', zh: '确认', ko: '확인', hi: 'पुष्टि करें', ar: 'تأكيد' })}</button>
                    </div>
                </div>
                </div>,
                document.body
            ) : null}
        </div>
    );
}
